import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { PlanService } from 'services';
import { Constants, Globals, checkPermission } from 'utils';
import { AddIcon, CloseIcon, EditIcon, TrashIcon } from 'assets/icons';
import { IAdvertisingRate, IClassifiedRate, ICommissionRate, IPlan, IState, ITeleconsultationRate } from "./types";
import { Modal } from "components";
import ModalCreatePlan from "./modal-create-plan";

interface Props extends RootState, RouteComponentProps {
	dispatch: any;
}

class Plans extends React.Component<Props> {
	state: IState = {
		showModal: false,
		selectedItem: undefined,
		plans: [],
		advertisingRates: [],
		classifiedRates: [],
		teleconsultationRates: [],
		commissionRates: [],
		iva: '',
	}

	componentDidMount() {
		this.props.dispatch({
			type: 'SET_HEADER',
			payload: {
				title: 'Planes y tarifas',
				onBack: null,
			},
		});

		this.props.dispatch({
			type: 'SET_SUBHEADER',
			payload: {
				title: '',
			},
		});

		this.load();
	}

	load = async (withoutLoading: boolean = false) => {
		const res: any = await PlanService.admin.get({
			withoutLoading,
		});

		this.setState({
			plans: res.plans || [],
			advertisingRates: res.advertisingRates || [],
			classifiedRates: res.classifiedRates || [],
			teleconsultationRates: res.teleconsultationRates || [],
			commissionRates: res.commissionRates || [],
			iva: res.iva?.percentage ?? '',
		});
	}

	handleChange = (field: string, value: string | number) => {
		this.setState({ [field]: value });
	}

	handlePlanChange = <T extends keyof IPlan, U extends IPlan[T]>(index: number, field: T, value: U) => {
		this.setState((prevState: IState) => ({
			plans: prevState.plans.map((item, itemIdx) =>
				itemIdx === index ? { ...item, [field]: value } : item,
			),
		}));
	}

	handleAdvertisingChange = <T extends keyof IAdvertisingRate, U extends IAdvertisingRate[T]>(index: number, field: T, value: U) => {
		this.setState((prevState: IState) => ({
			advertisingRates: prevState.advertisingRates.map((item, itemIdx) =>
				itemIdx === index ? { ...item, [field]: value } : item,
			),
		}));
	}

	handleClassifChange = <T extends keyof IClassifiedRate, U extends IClassifiedRate[T]>(index: number, field: T, value: U) => {
		this.setState((prevState: IState) => ({
			classifiedRates: prevState.classifiedRates.map((item, itemIdx) =>
				itemIdx === index ? { ...item, [field]: value } : item,
			),
		}));
	}

	handleTeleChange = <T extends keyof ITeleconsultationRate, U extends ITeleconsultationRate[T]>(index: number, field: T, value: U) => {
		this.setState((prevState: IState) => ({
			teleconsultationRates: prevState.teleconsultationRates.map((item, itemIdx) =>
				itemIdx === index ? { ...item, [field]: value } : item,
			),
		}));
	}

	handleCommissionChange = <T extends keyof ICommissionRate, U extends ICommissionRate[T]>(index: number, field: T, value: U) => {
		this.setState((prevState: IState) => ({
			commissionRates: prevState.commissionRates.map((item, itemIdx) =>
				itemIdx === index ? { ...item, [field]: value } : item,
			),
		}));
	}

	addTeleRate = () => {
		this.setState((prevState: IState) => ({
			teleconsultationRates: prevState.teleconsultationRates.concat([{ id: undefined, amount: '' }]),
		}));
	}

	onRemove = (item: IPlan) => {
		Globals.confirm(`¿Desea eliminar el plan ${item.name}?`, async () => {
			await PlanService.admin.remove({ plan_id: item.id });
			this.load(true);
		});
	}

	onRemoveTeleRate = (item: any) => {
		Globals.confirm(
			'¿Desea eliminar esta tarifa de teleconsulta?',
			async () => {
				try {
					await PlanService.admin.deleteTeleconsultationRate({ id: item.id });

					this.load(true);
				} catch (error) {
					console.error("Error eliminando tarifa:", error);
				}
			}
		);
	}

	onClose = () => {
		this.setState({ showModal: false, selectedItem: undefined });
	}

	submit = async (e?: any) => {
		if (e) e.preventDefault();

		const payload = {
			plans: this.state.plans.map((p: IPlan) => ({
				affiliate_type: p.affiliate_type ?? this.props.user.affiliate_type,
				plan_id: p.id,
				name: p.name,
				amount: p.amount,
				mensual_amount: p.mensual_amount,
				trimestral_amount: p.trimestral_amount,
				semestral_amount: p.semestral_amount,
			})),
			advertisingRates: this.state.advertisingRates.map((r: IAdvertisingRate) => ({
				id: r.id,
				amount: r.amount,
			})),
			classifiedRates: this.state.classifiedRates.map((r: IClassifiedRate) => ({
				id: r.id,
				amount: r.amount,
			})),
			teleconultationRates: this.state.teleconsultationRates.map((r: ITeleconsultationRate) => ({
				id: r.id,
				amount: r.amount,
			})),
			commissionRates: this.state.commissionRates.map((r: ICommissionRate) => ({
				id: r.id,
				amount: r.percentage,
			})),
			iva: this.state.iva,
		};

		await PlanService.admin.createOrUpdate(payload);

		Globals.showSuccess('Se han guardado los cambios correctamente');
		this.load(true);
	}

	render() {
		const canEdit = checkPermission(
			Constants.MODULES.PLANS,
			'write',
			this.props.user,
		);

		return (
			<div id="admin-plans-2">
				<Modal
					className="modal-create-plan"
					visible={this.state.showModal}
					withoutClose={true}
					onClose={this.onClose}
				>
					<ModalCreatePlan
						item={this.state.selectedItem}
						onClose={this.onClose}
						onSubmit={() => {
							this.load(true);
							this.onClose();
						}}
					/>
				</Modal>

				<div className="plans">
					<div className="plans-header">
						<p>Planes de Membresía</p>
						<button onClick={() => this.setState({ showModal: true })}>
							<img src={AddIcon} alt="Agregar" />
							<span>Agregar</span>
						</button>
					</div>
					<div className="plan-list">
						{this.state.plans.map((item, index) => (
							<div key={index} className="plan-item">
								<div className="plan-header">
									<img className="plan-cover" src={Globals.fromPhotos(item.icon)} alt="Plan" />
									<div className="plan-header-content">
										<div className="plan-actions">
											<button onClick={() => this.setState({ selectedItem: item, showModal: true })} title="Editar">
												<img src={EditIcon} alt="Editar" />
											</button>
											<button onClick={() => this.onRemove(item)} title="Eliminar">
												<img src={TrashIcon} alt="Eliminar" />
											</button>
										</div>
										<div className="plan-header-info">
											<p>{ item.name }</p>
											<p>{ item?.level?.name }</p>
										</div>
									</div>
								</div>

								<div className="plan-body">
									<div className="plan-pricing-annual">
										<p>Tarifa anual</p>
										<p>{ Globals.formatMiles(item.amount, true, '$') }</p>
									</div>
									<div className="plan-pricing">
										<div className="plan-price">
											<p>Tarifa{'\n'}semestral</p>
											<span>{ Globals.formatMiles(item.semestral_amount, true, '$') }</span>
										</div>
										<div className="plan-price">
											<p>Tarifa{'\n'}trimestral</p>
											<span>{ Globals.formatMiles(item.trimestral_amount, true, '$') }</span>
										</div>
										<div className="plan-price">
											<p>Tarifa{'\n'}mensual</p>
											<span>{ Globals.formatMiles(item.mensual_amount, true, '$') }</span>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
				<div className="fees">
					<div>
						<div className="fee-header">
							<p>Tarifas de Publicidad mensual</p>
						</div>

						<div className="fee-pos-list">
							{this.state.advertisingRates.map((item, index) => (
								<div key={index} className="fee-pos-item">
									<div className="fee-pos-img-wrapper">
										<img className="" src={Constants.BASE_PUBLIC+item.image} alt="Fee" />
									</div>
									<div className="fee-pos-content">
										<span>{ item.name }</span>
										<Input
											label="Tarifa"
											prepend="$"
											value={item.amount}
											onChange={e => this.handleAdvertisingChange(index, 'amount', Globals.sanitizeToDecimal(e.target.value))}
										/>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="divider" />

					<div>
						<div className="fee-header">
							<p>Tarifas de Clasificados mensual</p>
						</div>
						<div className="fee-classified-list">
							{this.state.classifiedRates.map((item, index) => (
								<Input
									key={index}
									label={`Tarifa ${index + 1}`}
									prepend="$"
									value={item.amount}
									onChange={e => this.handleClassifChange(index, 'amount', Globals.sanitizeToDecimal(e.target.value))}
								/>
							))}
						</div>
					</div>

					<div className="divider" />

					<div>
						<div className="fee-header">
							<p className="green">Tarifas de Teleconsultas</p>
							<button onClick={this.addTeleRate}>
								<img src={AddIcon} alt="Agregar" />
								<span>Agregar</span>
							</button>
						</div>

						<div className="fee-tele-list">
							{this.state.teleconsultationRates.map((item, index) => (
								<Input
									key={index}
									label={`Tarifa ${index + 1}`}
									prepend="$"
									append={
										<img
											src={TrashIcon}
											alt="Eliminar"
											className="pointer"
											style={{ width: '20px', height: '20px', cursor: 'pointer' }}
											onClick={() => this.onRemoveTeleRate(item)}
										/>
									}
									value={item.amount}
									onChange={e => this.handleTeleChange(index, 'amount', Globals.sanitizeToDecimal(e.target.value))}
								/>
							))}
						</div>
					</div>

					<div className="divider" />

					<div>
						<div className="fee-header">
							<p className="green">Comisiones</p>
						</div>
						<div className="fee-commission-list">
							{this.state.commissionRates.map((item, index) => (
								<Input
									key={index}
									label={item.type_text}
									prepend="%"
									value={item.percentage}
									onChange={e => this.handleCommissionChange(index, 'percentage', Globals.sanitizeToDecimal(e.target.value))}
								/>
							))}
						</div>
					</div>

					<div className="divider" />

					<div>
						<div className="fee-header">
							<p className="green">Impuesto</p>
						</div>
						<div>
							<Input
								label="IVA%"
								prepend="%"
								value={this.state.iva}
								onChange={e => this.handleChange('iva', Globals.sanitizeToDecimal(e.target.value))}
							/>
						</div>
					</div>

					{canEdit && (
						<div className="actions">
							<button
								className="button-primary"
								onClick={() => this.submit()}
							>
								Guardar
							</button>
						</div>
					)}
				</div>
			</div>
		);
	}
}

type InputProps = {
	label: string
	prepend?: string | React.JSX.Element
	append?: string | React.JSX.Element
	value: string | number
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const InputOLD = ({ label, prepend, append, value, onChange }: InputProps) => {
	return (
		<div className="input-base">
			<label>{ label }</label>
			<div className="input-wrapper">
				{!!prepend && (
					<div className="input-prepend">{ prepend }</div>
				)}
				<input
					type="number"
					value={String(value || '')}
					onChange={onChange}
				/>
				{!!append && (
					<div className="input-append">{ append }</div>
				)}
			</div>
		</div>
	)
}

const Input = ({ label, prepend, append, value, onChange }: InputProps) => {
	return (
		<div className="input-base">
			<label>{ label }</label>
			{/* 1. Agregamos display: flex para que queden en una sola linea */}
			<div className="input-wrapper" style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
				
				{!!prepend && (
					<div className="input-prepend">{ prepend }</div>
				)}
				
				<input
					type="number"
					value={String(value || '')}
					onChange={onChange}
					// 2. Importante: flex: 1 hace que el input ocupe el espacio disponible
					// pero respete el espacio del icono y del prepend.
					style={{ flex: 1, minWidth: 0 }} 
				/>
				
				{!!append && (
					// 3. Un pequeño margen a la izquierda para que no se pegue al input
					<div className="input-append" style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
						{ append }
					</div>
				)}
			</div>
		</div>
	)
}

export default connect((state: RootState) => {
	return {
		user: state.user,
	};
})(Plans);